import React, { useState } from "react";
import { Mail, Phone, MapPin, Send, Instagram, Youtube, Facebook } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const SUBJECTS = [
  "General Enquiry",
  "Artist Booking",
  "Collaboration / Partnership",
  "Membership Support",
  "Media / Press",
  "Technical Support",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General Enquiry", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received — we'll respond within 48 hours.");
      setForm({ name: "", email: "", phone: "", subject: "General Enquiry", message: "" });
    } catch {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <section className="bg-[#1F3864] text-white py-24 sm:py-28" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-4">Contact</div>
          <h1 className="font-display text-5xl sm:text-7xl leading-[0.95]">
            Let's <span className="italic text-[#C00000]">talk.</span>
          </h1>
        </div>
      </section>

      <section className="py-20 sm:py-24" data-testid="contact-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <div className="text-xs tracking-[0.2em] uppercase text-[#C00000] mb-3">Visit</div>
              <div className="flex items-start gap-3 text-gray-700">
                <MapPin className="w-5 h-5 text-[#1F3864] mt-0.5" />
                <div>189 Prince Street<br />San Francisco 407<br />Durban, KwaZulu-Natal, 4001<br />South Africa</div>
              </div>
            </div>
            <div>
              <div className="text-xs tracking-[0.2em] uppercase text-[#C00000] mb-3">Write</div>
              <a href="mailto:admin@nextgenpraise.com" className="flex items-center gap-3 text-gray-700 hover:text-[#1F3864]">
                <Mail className="w-5 h-5 text-[#1F3864]" /> admin@nextgenpraise.com
              </a>
            </div>
            <div>
              <div className="text-xs tracking-[0.2em] uppercase text-[#C00000] mb-3">Call</div>
              <a href="tel:+27735465752" className="flex items-center gap-3 text-gray-700 hover:text-[#1F3864]">
                <Phone className="w-5 h-5 text-[#1F3864]" /> +27 73 546 5752
              </a>
            </div>
            <div>
              <div className="text-xs tracking-[0.2em] uppercase text-[#C00000] mb-3">Follow</div>
              <div className="flex gap-2">
                <a href="#" className="w-10 h-10 border border-gray-300 hover:bg-[#1F3864] hover:text-white hover:border-[#1F3864] flex items-center justify-center transition-colors"><Instagram className="w-4 h-4" /></a>
                <a href="#" className="w-10 h-10 border border-gray-300 hover:bg-[#1F3864] hover:text-white hover:border-[#1F3864] flex items-center justify-center transition-colors"><Youtube className="w-4 h-4" /></a>
                <a href="#" className="w-10 h-10 border border-gray-300 hover:bg-[#1F3864] hover:text-white hover:border-[#1F3864] flex items-center justify-center transition-colors"><Facebook className="w-4 h-4" /></a>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-3 bg-white border border-gray-200 p-8 sm:p-10 space-y-4" data-testid="form-contact">
            <h2 className="font-display text-3xl text-[#1F3864] mb-2">Send us a message</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="w-full px-4 py-3 border border-gray-300 focus:border-[#1F3864] outline-none text-sm" data-testid="input-contact-name" />
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full px-4 py-3 border border-gray-300 focus:border-[#1F3864] outline-none text-sm" data-testid="input-contact-email" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" className="w-full px-4 py-3 border border-gray-300 focus:border-[#1F3864] outline-none text-sm" data-testid="input-contact-phone" />
              <select value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full px-4 py-3 border border-gray-300 focus:border-[#1F3864] outline-none text-sm bg-white" data-testid="select-contact-subject">
                {SUBJECTS.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Your message..." className="w-full px-4 py-3 border border-gray-300 focus:border-[#1F3864] outline-none text-sm" data-testid="input-contact-message" />
            <button type="submit" disabled={busy} className="btn-primary-red" data-testid="action-send-contact-button">
              <Send className="w-4 h-4" /> {busy ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>
      </section>

      <section className="h-[400px] bg-gray-200" data-testid="contact-map">
        <iframe
          title="Map"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14426.45193891236!2d31.021!3d-29.857!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1ef7a95ff84afba5%3A0x77c36a3d02ff3c1f!2sDurban%2C%20South%20Africa!5e0!3m2!1sen!2sza!4v1700000000"
          className="w-full h-full border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </section>
    </div>
  );
}
